/**
 * Evidence and the timeline — the two halves of "what is on the record".
 *
 * ── Evidence is references, and the panel says so ───────────────────────────
 *
 * `Incident.evidence[]` is `{kind, ref, captured_at}`. `openapi.yaml` is explicit
 * that the bytes live in object storage under the company prefix and are fetched
 * through short-lived signed URLs — and **no session endpoint issues one**. So the
 * panel prints the reference and names the fact; it does not render a link that would
 * `404`, because a dead link on an investigation record reads as missing evidence
 * rather than as a missing endpoint.
 *
 * `redacted: true` renders a sentence naming what was withheld and why. Redaction on
 * an incident is one thing — the alert's **group key**, the subject the alert was
 * about (`app_name=…`, `domain=…`) — dropped whole rather than trimmed, because a
 * trimmed key is still a well-formed key and two different subjects would collapse
 * onto one displayed value. A panel that silently dropped the entry would report a
 * shorter investigation than the one that happened.
 *
 * ── The timeline shows what the wire carries, and says what it does not ─────
 *
 * A rendered `IncidentComment` is `{id, author_id, body, created_at}` and **nothing
 * else**. There is no `comment_type`, no `from_state`/`to_state` and no `metadata`,
 * so this panel can show *when*, *who* and *what was written*, and it **cannot** show
 * which transition a row records. It does not guess one from the body text: a note
 * that happens to read like a state name is still a note somebody typed.
 *
 * That limitation is printed on the panel rather than only recorded in a report,
 * because a reader who assumes the timeline is the transition history will read a
 * gap in it as a gap in the record. The history itself is complete — a CONSTRAINT
 * TRIGGER refuses to commit a transition without its row — it is the *wire* that does
 * not publish the pair.
 *
 * `author_id: null` is the **system**, not an unattributed blank: the column is
 * nullable precisely so that "the system did this" is not put against whoever
 * happened to be on shift.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type { MessageKey } from '../../i18n/messages';
import type { IncidentComment, IncidentEvidence } from '../../api/types';
import { STATE_LABEL } from '../../incidents/labels';
import { knownPair, transitionOf } from '../../incidents/timeline';
import { EmptyState } from '../../states/EmptyState';

const KIND_LABEL: Readonly<Record<IncidentEvidence['kind'], MessageKey>> = {
  activity_event: 'incidents.evidence.kind.activityEvent',
  time_entry: 'incidents.evidence.kind.timeEntry',
  attendance_day: 'incidents.evidence.kind.attendanceDay',
  alert: 'incidents.evidence.kind.alert',
  file: 'incidents.evidence.kind.file',
  note: 'incidents.evidence.kind.note',
};

export interface EvidencePanelProps {
  readonly evidence: readonly IncidentEvidence[];
  readonly redacted: boolean;
  readonly timezone: string;
}

export function EvidencePanel({ evidence, redacted, timezone }: EvidencePanelProps): ReactNode {
  const { t, locale } = useI18n();
  return (
    <section className="wp-section" aria-labelledby="l5-evidence-heading" data-testid="l5-evidence">
      <h3 className="wp-section__title" id="l5-evidence-heading">
        {t('incidents.evidence.title')}
      </h3>
      <p className="wp-small wp-muted" data-testid="l5-evidence-refs-only">
        {t('incidents.evidence.refsOnly')}
      </p>

      {redacted ? (
        <p className="wp-small" data-testid="l5-evidence-redacted">
          {t('incidents.evidence.redacted')}
        </p>
      ) : null}

      {evidence.length === 0 ? (
        <EmptyState
          title={t('incidents.evidence.empty.title')}
          description={t('incidents.evidence.empty.body')}
          testId="l5-evidence-empty"
        />
      ) : (
        <ul className="wp-c-ruled" data-testid="l5-evidence-list">
          {evidence.map((entry, index) => (
            <li
              className="wp-c-ruled__row wp-c-ruled__row--stack"
              key={`${entry.kind}:${entry.ref}:${String(index)}`}
            >
              <span>{t(KIND_LABEL[entry.kind])}</span> <code className="wp-mono">{entry.ref}</code>
              {typeof entry.captured_at === 'string' ? (
                <span className="wp-small wp-muted">
                  {' · '}
                  {formatInstant(entry.captured_at, locale, timezone)}
                </span>
              ) : (
                // `null` or absent is "the server published no capture instant", and
                // it is said rather than left as a blank cell that reads as "just now".
                <span className="wp-small wp-muted">
                  {' · '}
                  {t('incidents.evidence.noCapturedAt')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Which move a timeline row records (G19) — three answers, and none of them a dash.
 *
 * The panel used to carry a sentence saying it could not show this. That was true when
 * Task 53 wrote it and stopped being true at `d3a0e59`, which put `from_state` and
 * `to_state` on the wire; the sentence is gone rather than softened, because a UI that
 * keeps apologising for a limitation somebody has fixed tells the reader to stop
 * looking.
 *
 * `web/src/incidents/timeline.ts` does the reading. What matters here is that the
 * `null` case gets **a sentence of its own** — *"this row records no state change"* —
 * rather than an empty cell or a dash. `null` on this pair is a positive fact about a
 * note, and rendering it the same way as a broken pair would leave a reader unable to
 * tell a quiet incident from a record with a hole in it, on the one panel whose whole
 * job is to be the record.
 *
 * `data-transition` carries the discriminant so a mutation swapping one branch for its
 * neighbour dies on the attribute rather than on a sentence that a neighbouring
 * element might also contain — the trap Tasks 45 and 53 each hit once.
 */
function TransitionLine({ comment }: { readonly comment: IncidentComment }): ReactNode {
  const { t } = useI18n();
  const transition = transitionOf(comment);
  const pair = knownPair(transition);

  if (transition.kind === 'none') {
    return (
      <p
        className="wp-small wp-muted"
        data-testid={`l5-timeline-move-${comment.id}`}
        data-transition="none"
      >
        {t('incidents.timeline.noTransition')}
      </p>
    );
  }

  if (transition.kind === 'half_pair') {
    return (
      <p
        className="wp-small"
        role="alert"
        data-testid={`l5-timeline-move-${comment.id}`}
        data-transition="half_pair"
      >
        {t('incidents.timeline.halfPair', {
          from: transition.from ?? 'null',
          to: transition.to ?? 'null',
        })}
      </p>
    );
  }

  // Both present. Labelled where this client knows the names, and printed **raw**
  // where it does not: a server one state ahead of this bundle must still show the
  // move rather than have it hidden by a client that is behind.
  const from = pair === null ? transition.from : t(STATE_LABEL[pair.from]);
  const to = pair === null ? transition.to : t(STATE_LABEL[pair.to]);
  return (
    <p
      className="wp-small"
      data-testid={`l5-timeline-move-${comment.id}`}
      data-transition="transition"
      data-known={transition.known ? 'yes' : 'no'}
    >
      {transition.known
        ? t('incidents.timeline.transition', { from, to })
        : t('incidents.timeline.transitionUnknown', { from, to })}
    </p>
  );
}

export interface TimelineProps {
  readonly comments: readonly IncidentComment[] | undefined;
  readonly timezone: string;
}

export function Timeline({ comments, timezone }: TimelineProps): ReactNode {
  const { t, locale } = useI18n();
  return (
    <section className="wp-section" aria-labelledby="l5-timeline-heading" data-testid="l5-timeline">
      <h3 className="wp-section__title" id="l5-timeline-heading">
        {t('incidents.timeline.title')}
      </h3>
      <p className="wp-small wp-muted" data-testid="l5-timeline-intro">
        {t('incidents.timeline.intro')}
      </p>

      {comments === undefined ? (
        // The list representation omits `comments` entirely; the detail read carries
        // them. Absent and empty are different facts and are two different sentences:
        // "not read yet" is not "nobody has written anything".
        <p data-testid="l5-timeline-not-read">{t('incidents.timeline.notRead')}</p>
      ) : comments.length === 0 ? (
        <EmptyState
          title={t('incidents.timeline.empty.title')}
          description={t('incidents.timeline.empty.body')}
          testId="l5-timeline-empty"
        />
      ) : (
        <ol className="wp-c-ruled" data-testid="l5-timeline-list">
          {comments.map((comment) => (
            <li
              className="wp-c-ruled__row wp-c-ruled__row--stack"
              key={comment.id}
              data-testid={`l5-timeline-${comment.id}`}
            >
              <p className="wp-small wp-muted">
                {formatInstant(comment.created_at, locale, timezone)}
                {' · '}
                {comment.author_id === null
                  ? t('incidents.timeline.system')
                  : t('incidents.timeline.author', { id: comment.author_id })}
              </p>
              <p>{comment.body}</p>
              <TransitionLine comment={comment} />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
